import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../config';
import { formatRelativeTime } from '../utils/date';
import { formatLargeNumber } from '../utils/numberFormat';
import { SnackbarContext } from '../ui/SnackbarProvider';

interface ColumnProfile {
  name: string;
  type: string;
  cardinality: number;
  null_percentage: number;
  min?: number | string;
  max?: number | string;
  is_categorical?: boolean;
  distinct_values?: Array<{
    value: string;
    count: number;
    percentage: number;
  }>;
}

interface TableProfile {
  entity_id: string;
  profile_id: string | null;  // Primary key for fetching details
  status: 'completed' | 'failed' | 'not_profiled';
  total_rows: number | null;
  sampled_rows: number | null;
  sampling_method: string | null;
  profiled_at: string | null;
  error_message: string | null;
  column_count: number;
}

interface TableProfileDetail extends TableProfile {
  columns: ColumnProfile[];
}

interface ProfileSummary {
  total_tables: number;
  profiled: number;
  failed: number;
  not_profiled: number;
}

interface ProfilingTabProps {
  connectorId: string;
  tenantId: string;
}

export function ProfilingTab({ connectorId, tenantId }: ProfilingTabProps) {
  const { notify } = React.useContext(SnackbarContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ProfileSummary | null>(null);
  const [tables, setTables] = useState<TableProfile[]>([]);
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [profiling, setProfiling] = useState(false);
  const [forceReprofile, setForceReprofile] = useState(false);
  
  // Track loaded column details for each table
  const [tableDetails, setTableDetails] = useState<Map<string, ColumnProfile[]>>(new Map());
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set());

  const fetchProfiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const response = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/profiles`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch profiles');
      }

      const data = await response.json();
      setSummary(data.summary);
      setTables(data.tables);
    } catch (err: any) {
      setError(err.message || 'Failed to load profiling data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, [connectorId, tenantId]);

  const fetchEntityDetails = async (entityId: string, profileId: string) => {
    // Skip if already loaded or currently loading
    if (tableDetails.has(entityId) || loadingDetails.has(entityId)) {
      return;
    }

    setLoadingDetails(prev => new Set(prev).add(entityId));
    
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const response = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/profiles/${profileId}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch entity details');
      }

      const data = await response.json();
      setTableDetails(prev => new Map(prev).set(entityId, data.columns || []));
    } catch (err: any) {
      notify(err.message || 'Failed to load column details', 'error');
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(entityId);
        return newSet;
      });
    }
  };

  const handleToggleExpand = async (entityId: string, table: TableProfile) => {
    const newExpanded = new Set(expandedTables);
    if (newExpanded.has(entityId)) {
      newExpanded.delete(entityId);
    } else {
      newExpanded.add(entityId);
      // Fetch details if table is profiled and we don't have details yet
      if (table.status === 'completed' && table.profile_id && !tableDetails.has(entityId)) {
        await fetchEntityDetails(entityId, table.profile_id);
      }
    }
    setExpandedTables(newExpanded);
  };

  const handleProfile = async () => {
    setProfiling(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      
      const entityIds = null;
      
      const response = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/profile-schema`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            entity_ids: entityIds,
            force: forceReprofile
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to start profiling');
      }

      notify('Profiling started for all tables', 'success');

      // Reset state
      setForceReprofile(false);

      // Refresh profiles after a short delay
      setTimeout(() => {
        fetchProfiles();
      }, 2000);
    } catch (err: any) {
      notify(err.message || 'Failed to start profiling', 'error');
      setError(err.message || 'Failed to start profiling');
    } finally {
      setProfiling(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />;
      case 'failed':
        return <ErrorIcon sx={{ fontSize: 20, color: 'error.main' }} />;
      case 'not_profiled':
        return <HourglassEmptyIcon sx={{ fontSize: 20, color: 'text.disabled' }} />;
      default:
        return null;
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'completed':
        return <Chip label="Profiled" size="small" color="success" />;
      case 'failed':
        return <Chip label="Failed" size="small" color="error" />;
      case 'not_profiled':
        return <Chip label="Not Profiled" size="small" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            📊 Profile Summary
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {summary?.total_tables || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Tables
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                {summary?.profiled || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ✓ Profiled
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                {summary?.failed || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ⚠ Failed
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.disabled' }}>
                {summary?.not_profiled || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Not Profiled
              </Typography>
            </Paper>
          </Stack>

        </CardContent>
      </Card>

      {/* Tables List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {tables.map((table) => {
          const isExpanded = expandedTables.has(table.entity_id);

          return (
            <Card key={table.entity_id} variant="outlined" sx={{ borderRadius: 1.5 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    onClick={() => handleToggleExpand(table.entity_id, table)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {getStatusIcon(table.status)}
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {table.entity_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {table.total_rows !== null
                            ? `${formatLargeNumber(table.total_rows)} rows • ${table.column_count} columns`
                            : `${table.column_count} columns`}
                          {table.profiled_at && ` • Profiled ${formatRelativeTime(table.profiled_at)}`}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusChip(table.status)}
                      <IconButton
                        size="small"
                        sx={{
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>

                {/* Error Message */}
                {table.status === 'failed' && table.error_message && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {table.error_message}
                  </Alert>
                )}

                {/* Column Details */}
                <Collapse in={isExpanded}>
                  <Box sx={{ mt: 2 }}>
                    {loadingDetails.has(table.entity_id) ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : tableDetails.has(table.entity_id) && tableDetails.get(table.entity_id)!.length > 0 ? (
                      <>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Column Profiles
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Column</strong></TableCell>
                              <TableCell><strong>Profiled Type</strong></TableCell>
                              <TableCell><strong>Cardinality</strong></TableCell>
                              <TableCell><strong>Null %</strong></TableCell>
                              <TableCell><strong>Details</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tableDetails.get(table.entity_id)!.map((column: ColumnProfile, colIdx: number) => (
                              <React.Fragment key={column.name || `col-${colIdx}`}>
                                <TableRow>
                                  <TableCell>{column.name || 'Unknown'}</TableCell>
                                  <TableCell>
                                    <Chip label={column.type || 'Unknown'} size="small" variant="outlined" />
                                  </TableCell>
                                  <TableCell>{formatLargeNumber(column.cardinality)}</TableCell>
                                  <TableCell>{column.null_percentage != null ? column.null_percentage.toFixed(1) + '%' : 'N/A'}</TableCell>
                                  <TableCell>
                                    {column.type === 'Numeric' && column.min !== undefined && column.max !== undefined && (
                                      <Typography variant="caption" color="text.secondary">
                                        Min: {column.min} • Max: {column.max}
                                      </Typography>
                                    )}
                                    {column.type === 'DateTime' && column.min && column.max && (
                                      <Typography variant="caption" color="text.secondary">
                                        {column.min} to {column.max}
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                                {/* Categorical Distinct Values */}
                                {column.is_categorical && column.distinct_values && column.distinct_values.length > 0 && (
                                  <TableRow>
                                    <TableCell colSpan={5} sx={{ bgcolor: 'background.default', py: 1 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                        Distinct Values:
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {column.distinct_values.map((dv: any, idx: number) => (
                                          <Typography key={idx} variant="caption" color="text.secondary">
                                            • <strong>{dv.value ?? 'N/A'}</strong>: {dv.count?.toLocaleString() ?? 'N/A'} ({dv.percentage != null ? dv.percentage.toFixed(1) + '%' : 'N/A'})
                                          </Typography>
                                        ))}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        No column profiles available
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>

    </Box>
  );
}
